import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { archiveAccount, editAccount, getAccountById, makeAccountActive } from 'src/app/shared/services/AccountService'
import { IAccount, ICreateAccount, IEditAccount, IMakeAccountActive } from 'src/app/shared/models/accountSetup.model'
import { useTranslation } from 'react-i18next'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { useNavigate, useParams } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import { EditAccountContext } from './index.context'
import { modalRef } from '../components/CustomConfirmationModal'
import { invalidEditAccountRef } from '../components/InvalidEditAccountModal'
import { EditAccountFormValues } from '../models'
import { ACCOUNT_STATUS, CHECK_ACH_DEFAULTS, PROCESSING_TYPES, defaultEditValues } from '../constants'
import { convertTime } from '../utils'
import lodash from 'lodash'
import { notification } from 'antd'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { getClientById } from 'src/app/shared/services/ClientSetupService'
import { IClient } from 'src/app/shared/models/clientSetup.model'
import { payeeMatchWarningRef } from '../components/PayeeMatchWarningModal'
import { hasExceptionsByAccount } from 'src/app/shared/services/TransactionService'

export type IDuplicatedAccount = Pick<ICreateAccount.Payload, 'accountName' | 'accountNumber'> & {
  isActive: boolean
  duplicateType: Array<string>
}

export type IArchiveAccount = Pick<IAccount, 'accountName' | 'accountNumber'>

export type IDuplicatedAccounts = Array<IDuplicatedAccount>

export type IToastType = 'new' | 'active' | 'archive' | 'update'

export type IMessage = {
  displayToast: boolean
  accountActiveMessage: string
}

export const EditAccountProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const { t } = useTranslation()
  const { id } = useParams()
  const [api, contextHolder] = notification.useNotification()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const [account, setAccount] = useState<IAccount | null>(null)
  const [client, setClient] = useState<IClient | null>(null)
  const [switchChanged, setSwitchChanged] = useState<boolean>(false)
  const [isFetched, setIsFetched] = useState<boolean>(false)
  const [hasAchExceptions, setHasAchExceptions] = useState<boolean>(false);
  const [hasCheckExceptions, setHasCheckExceptions] = useState<boolean>(false);

  const form = useForm<EditAccountFormValues>({
    defaultValues: defaultEditValues,
    mode: 'all',
  })

  const handleGetClient = useCallback(
    async (id: string) => {
      try {
        setLoading(true)
        const clientResponse = await getClientById({
          clientId: id,
        })
        if (clientResponse.statusCode === STATUS_CODES.SUCCESS) {
          setClient(clientResponse.data)
          form.clearErrors()
        }
      } catch (error) {
        console.log('ERROR: ', error)
      } finally {
        setLoading(false)
      }
    },
    [form],
  )

  useEffect(() => {
    if (account?.clientId) handleGetClient(account?.clientId)
  }, [account?.clientId, handleGetClient])

  const checkExceptionProcessingType = useWatch({
    control: form.control,
    name: 'checkExceptionProcessingType',
  })

  const achExceptionProcessingType = useWatch({
    control: form.control,
    name: 'achExceptionProcessingType',
  })

  const validateForm = useCallback(async () => {
    return await form.trigger()
  }, [form])

  const resetState = useCallback(() => {
    form.clearErrors()
    modalRef.current?.hideModal()
  }, [form])

  const handleSetAccount = useCallback(
    (account: IAccount) => {
      Object.keys(account).forEach((prop: any) => {
        const value = (account as IAccount)[prop as keyof IAccount]
        if (prop === 'createdAt') {
          form.setValue(
            prop as any,
            convertTime({
              date: value,
            }),
          )
        } else if (prop === 'status') {
          form.setValue('isArchiveAccount', account['status'] === ACCOUNT_STATUS.ARCHIVE)
        } else {
          form.setValue(prop as any, value)
        }
      })
    },
    [form],
  )

  const getHasExceptions = useCallback(async () => {
    if (id) {
      const res = await hasExceptionsByAccount(id);
      setHasAchExceptions(res.data.hasAchExceptions);
      setHasCheckExceptions(res.data.hasCheckExceptions);
    }
  }, [id]);


  const getAccountDetail = useCallback(async () => {
    try {
      setIsFetched(false)
      if (id) {
        setLoading(true)
        const accountResponse = await getAccountById({
          accountId: id,
        })
        if (accountResponse.statusCode === STATUS_CODES.SUCCESS) {
          handleSetAccount(accountResponse.data)
          setAccount(accountResponse.data)
        }
      }
    } catch (error: any) {
      if ([SERVER_STATUS_CODES.PPI11_ACCOUNT_NOT_EXIST].includes(error?.statusCode)) {
        api.error({
          message: t('common:error'),
          description: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_NOT_EXIST}`),
          placement: 'topRight',
        })
      }
    } finally {
      setLoading(false)
      setIsFetched(true)
    }
  }, [api, handleSetAccount, id, t])

  const handleShowMessage = useCallback(
    (messages: IMessage[], backtoLanding: boolean = true) => {
      if (backtoLanding) {
        navigate(menuUrls.accountSetup, {
          state: messages,
        })
      } else {
        lodash.uniqBy(messages, 'accountActiveMessage').forEach((item) => {
          api.success({
            message: <RichTextField richText={item.accountActiveMessage} />,
          })
        })
      }
    },
    [api, navigate],
  )

  const formatDataBeforeSubmit = useCallback((data: IEditAccount.Payload['data']) => {
    return {
      ...data,
      accountName: data.accountName.trim(),
      accountNumber: data.accountNumber.trim(),
      checkDecision: data.checkDecision ?? '',
      checkReason: data.checkReason ?? '',
      achDecision: data.achDecision ?? '',
      achReason: data.achReason ?? '',
    }
  }, [])

  const handleMakeActiveError = useCallback(
    (field: 'accountName' | 'accountNumber' = 'accountName') => {
      form.setError(field, {
        type: 'pattern',
        message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_ACTIVE}`),
      })
      modalRef.current?.hideModal()
    },
    [form, t],
  )

  const handleError = useCallback(
    (error: any) => {
      if ([SYSTEM_MESSAGE_IDS.ACCOUNT_ACTIVE, SERVER_STATUS_CODES.PPI12_ACCOUNT_EXIST].includes(error?.statusCode)) {
        handleMakeActiveError()
      }
      if ([SYSTEM_MESSAGE_IDS.CLIENT_ARCHIVED].includes(error?.statusCode)) {
        api.error({
          message: t('common:error'),
          description: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CLIENT_ARCHIVED}`),
          placement: 'topRight',
        })
      }
      modalRef.current?.hideModal()
    },
    [api, handleMakeActiveError, t],
  )

  const getMessage = useCallback(
    (accountName: string, type: IToastType) => {
      switch (type) {
        case 'active':
          return t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_ACTIVE_SUCCESSFULLY}`, {
            accountName: accountName,
          })
        case 'new':
          return t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_CREATED_SUCCESSFULLY}`, {
            accountName: accountName,
          })
        case 'archive':
          return t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_ARCHIVE_SUCCESSFULLY}`, {
            accountName: accountName,
          })
        case 'update':
          return t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_UPDATED_SUCCESSFULLY}`, {
            accountName: accountName,
          })
        default:
          return ''
      }
    },
    [t],
  )

  const onArchiveAccount = useCallback(
    async (account: IArchiveAccount, backtoLanding?: boolean) => {
      try {
        setLoading(true)
        const archiveResponse = await archiveAccount({
          archiveAccountNumber: account.accountNumber,
        })
        if (archiveResponse.data) {
          handleShowMessage(
            [
              {
                displayToast: true,
                accountActiveMessage: getMessage(account.accountName, 'archive'),
              },
              // {
              //   displayToast: true,
              //   accountActiveMessage: getMessage(account.accountName, 'update'),
              // },
            ],
            backtoLanding,
          )
          form.reset()
          await getAccountDetail()
          modalRef.current?.hideModal()
        }
      } catch (error: any) {
        console.log('ERROR: ', error)
        handleError(error)
      } finally {
        setLoading(false)
      }
    },
    [form, getAccountDetail, getMessage, handleError, handleShowMessage],
  )

  const handleMakeAccountArchive = useCallback(
    (account: IArchiveAccount, backtoLanding?: boolean) => {
      modalRef.current?.showModal({
        title: t('AccountSetup:new_account_form.confirmation_modal.title'),
        description: t('AccountSetup:new_account_form.confirmation_modal.archive_description', {
          accountName: account.accountName,
        }),
        action: () => onArchiveAccount(account, backtoLanding),
        cancel: () => {
          resetState()
          modalRef.current?.hideModal?.()
        },
      })
    },
    [onArchiveAccount, resetState, t],
  )

  const onMakeActive = useCallback(
    async (account: IDuplicatedAccount, options?: { archiveAccount: IArchiveAccount; backtoLanding: boolean }) => {
      try {
        setLoading(true)
        const formValues = formatDataBeforeSubmit(form.getValues() as any)
        const data: IMakeAccountActive.Payload = {
          makeActiveAccountParams: formValues,
          makeActiveAccountNumber: account.accountNumber,
        }
        const activeResponse = await makeAccountActive(data)

        // handle open next duplicated account
        if (activeResponse.data) {
          if (!options?.archiveAccount) {
            handleShowMessage(
              [
                {
                  displayToast: true,
                  accountActiveMessage: getMessage(account.accountName, 'active'),
                },
                // {
                //   displayToast: true,
                //   accountActiveMessage: getMessage(account.accountName, 'update'),
                // },
              ],
              options?.backtoLanding,
            )
            await getAccountDetail()
            modalRef.current?.hideModal()
          } else {
            handleMakeAccountArchive(options.archiveAccount, options.backtoLanding)
          }
        }
      } catch (error: any) {
        console.log('ERROR: ', error)
        handleError(error)
      } finally {
        setLoading(false)
      }
    },
    [
      form,
      formatDataBeforeSubmit,
      getAccountDetail,
      getMessage,
      handleError,
      handleMakeAccountArchive,
      handleShowMessage,
    ],
  )

  const handleMakeAccountActive = useCallback(
    (account: IDuplicatedAccount, options?: { archiveAccount: IArchiveAccount; backtoLanding: boolean }) => {
      if (!account.isActive) {
        modalRef.current?.showModal({
          title: t('AccountSetup:new_account_form.confirmation_modal.title'),
          description: t('AccountSetup:new_account_form.confirmation_modal.active_description', {
            accountName: account.accountName,
          }),
          action: () => onMakeActive(account, options),
          cancel: () => {
            modalRef.current?.hideModal()
            resetState()
          },
        })
      } else {
        // duplicated account is active
        account.duplicateType.forEach((item) => {
          handleMakeActiveError(item === 'Account Number' ? 'accountNumber' : 'accountName')
        })
      }
    },
    [t, onMakeActive, resetState, handleMakeActiveError],
  )

  const continueSubmit = useCallback(async (values: EditAccountFormValues) => {
    setLoading(true)
    try {
      if (
        (client?.defaultCheckExceptionCreationType === PROCESSING_TYPES.NOCREATE &&
          form.getValues('checkExceptionProcessingType') === PROCESSING_TYPES.CREATE) ||
        (client?.defaultAchExceptionCreationType === PROCESSING_TYPES.NOCREATE &&
          form.getValues('achExceptionProcessingType') === PROCESSING_TYPES.CREATE)
      ) {
        invalidEditAccountRef.current?.showModal({
          title: t('AccountSetup:new_account_form.invalid_update_modal.title'),
          description: t('AccountSetup:new_account_form.invalid_update_modal.account_conflict_with_client'),
          action: invalidEditAccountRef.current?.hideModal,
          cancel: () => {},
        })
        return
      } else if (!client?.payeeMatch && form.getValues('payeeMatch')) {
        payeeMatchWarningRef.current?.show()
        return
      }

      const data: IEditAccount.Payload['data'] = formatDataBeforeSubmit({
        ...values,
        status: account?.status ?? 0,
      })

      const accountResponse = await editAccount({
        accountId: id ?? '',
        data: data,
      })

      if (!accountResponse.data.isSuccess) {
        navigate(`${menuUrls.editAccount}/${accountResponse.data.duplicateId}`)
      } else {
        const isArchived = form.getValues('isArchiveAccount')

        if (switchChanged) {
          isArchived
            ? handleMakeAccountArchive({
                accountName: data.accountName,
                accountNumber: data.accountNumber,
              })
            : handleMakeAccountActive({
                accountName: data.accountName,
                accountNumber: data.accountNumber,
                duplicateType: [],
                isActive: false,
              })
        } else {
          navigate(menuUrls.accountSetup, {
            state: [
              {
                displayToast: true,
                accountActiveMessage: getMessage(data.accountName, 'update'),
              },
            ],
          })
        }
      }
    } catch (error: any) {
      if ([SYSTEM_MESSAGE_IDS.ACCOUNT_ACTIVE, SYSTEM_MESSAGE_IDS.ACCOUNT_IN_SYSTEM].includes(error.statusCode)) {
        form.setError('accountName', {
          message: t(`SystemMessages:${error.statusCode}`),
          type: 'pattern',
        })
      }

      if (error.statusCode === SERVER_STATUS_CODES.PPI10_CLIENT_ARCHIVE) {
        api.error({
          message: '',
          description: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CLIENT_ARCHIVED}`),
          placement: 'topRight',
        })
      }

      if (error.statusCode === SYSTEM_MESSAGE_IDS.ACCOUNT_BELONGS_TO_ANOTHER_CLIENT) {
        api.error({
          message: '',
          description: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACCOUNT_BELONGS_TO_ANOTHER_CLIENT}`),
          placement: 'topRight',
        })
      }

      // Surface previously unhandled backend validation codes so the save never fails silently.
      const handledCodes: string[] = [
        SYSTEM_MESSAGE_IDS.ACCOUNT_ACTIVE,
        SYSTEM_MESSAGE_IDS.ACCOUNT_IN_SYSTEM,
        SERVER_STATUS_CODES.PPI10_CLIENT_ARCHIVE,
        SYSTEM_MESSAGE_IDS.ACCOUNT_BELONGS_TO_ANOTHER_CLIENT,
      ]
      const toastCodes: string[] = [
        SERVER_STATUS_CODES.PPI21_DUPLICATE_ACCOUNT,
        SERVER_STATUS_CODES.PPI13_NULL_OR_EMPTY_VALUE,
        SERVER_STATUS_CODES.PPI9_CLIENT_NOT_EXIST,
        SERVER_STATUS_CODES.PPI11_ACCOUNT_NOT_EXIST,
        SERVER_STATUS_CODES.PPP05_COLUMN_IS_REQUIRED,
      ]

      if (error?.statusCode && toastCodes.includes(error.statusCode)) {
        api.error({
          message: '',
          description: t(`SystemMessages:${error.statusCode}`),
          placement: 'topRight',
        })
      } else if (error?.statusCode && !handledCodes.includes(error.statusCode)) {
        api.error({
          message: '',
          description: t(`SystemMessages:GENERIC_SAVE_ERROR`),
          placement: 'topRight',
        })
      }
    } finally {
      setLoading(false)
    }
  }, [
    client,
    form,
    account?.status,
    id,
    switchChanged,
    handleMakeAccountArchive,
    handleMakeAccountActive,
    getMessage,
    navigate,
    api,
    t,
    formatDataBeforeSubmit,
  ])

  const onSubmit: SubmitHandler<EditAccountFormValues> = useCallback(
  async (values) => {
    if (!validateForm()) return

    if (
      (form.getValues('checkExceptionProcessingType') === PROCESSING_TYPES.NOCREATE && hasCheckExceptions) ||
      (form.getValues('achExceptionProcessingType') === PROCESSING_TYPES.NOCREATE && hasAchExceptions)
    ) {
      modalRef.current?.showModal({
        title: t('AccountSetup:new_account_form.confirmation_modal.title'),
        description: t('AccountSetup:new_account_form.confirmation_modal.no_exceptions_description'),
        action: async () => {
          modalRef.current?.hideModal()
          await continueSubmit(values)
        },
        cancel: () => modalRef.current?.hideModal(),
      })
      return
    }

    await continueSubmit(values)
  },
  [validateForm, form, hasCheckExceptions, hasAchExceptions, continueSubmit, t]
)


  const prevCheckProcessingType = useRef<string | undefined>(undefined)
  const prevAchProcessingType = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (checkExceptionProcessingType === PROCESSING_TYPES.NOCREATE) {
      form.setValue('checkDecision', '')
      form.setValue('checkReason', '')
      form.setValue('payeeMatch', false)
      form.setValue('staleDatedDays', '')
      form.clearErrors('checkDecision')
      form.clearErrors('checkReason')
      form.clearErrors('payeeMatch')
      form.clearErrors('staleDatedDays')
    } else if (
      prevCheckProcessingType.current === PROCESSING_TYPES.NOCREATE &&
      checkExceptionProcessingType === PROCESSING_TYPES.CREATE
    ) {
      form.setValue('checkDecision', CHECK_ACH_DEFAULTS.checkDecision)
      form.setValue('checkReason', CHECK_ACH_DEFAULTS.checkReason)
      form.setValue('staleDatedDays', CHECK_ACH_DEFAULTS.staleDatedDays)
      form.setValue('payeeMatch', CHECK_ACH_DEFAULTS.payeeMatch && !!client?.payeeMatch)
    }
    prevCheckProcessingType.current = checkExceptionProcessingType
  }, [checkExceptionProcessingType, client, form, form.setValue])

  useEffect(() => {
    if (achExceptionProcessingType === PROCESSING_TYPES.NOCREATE) {
      form.setValue('achDecision', '')
      form.setValue('achReason', '')
      form.clearErrors('achDecision')
      form.clearErrors('achReason')
    } else if (
      prevAchProcessingType.current === PROCESSING_TYPES.NOCREATE &&
      achExceptionProcessingType === PROCESSING_TYPES.CREATE
    ) {
      form.setValue('achDecision', CHECK_ACH_DEFAULTS.achDecision)
      form.setValue('achReason', CHECK_ACH_DEFAULTS.achReason)
    }
    prevAchProcessingType.current = achExceptionProcessingType
  }, [achExceptionProcessingType, form, form.setValue])

  // set service type parameter
  useEffect(() => {
    const isCreatingCheck = checkExceptionProcessingType === PROCESSING_TYPES.CREATE
    const isCreatingACH = achExceptionProcessingType === PROCESSING_TYPES.CREATE

    if (isCreatingCheck && isCreatingACH) {
      form.setValue('serviceTypeParameter', 'Both')
    } else if (isCreatingCheck && !isCreatingACH) {
      // check only
      form.setValue('serviceTypeParameter', 'Check')
    } else if (!isCreatingCheck && isCreatingACH) {
      // ACH only
      form.setValue('serviceTypeParameter', 'ACH')
    } else {
      form.setValue('serviceTypeParameter', '')
    }
  }, [achExceptionProcessingType, checkExceptionProcessingType, form, t])

  // get account detail when loading
  useEffect(() => {
    getAccountDetail()
    getHasExceptions()
  }, [id, getAccountDetail, getHasExceptions])

  useEffect(() => {
  }, [hasCheckExceptions])

  // set loading in modal
  useEffect(() => {
    modalRef.current?.setLoading(loading)
  }, [loading])

  // reset all state when page is destroyed
  useEffect(() => {
    return () => {
      resetState()
    }
  }, [resetState])

  return (
    <>
      {contextHolder}
      <EditAccountContext.Provider
        value={{
          form,
          isFetched,
          checkExceptionProcessingType,
          achExceptionProcessingType,
          loading,
          modalRef,
          payeeMatchWarningRef,
          invalidEditAccountRef,
          setSwitchChanged,
          onSubmit,
          client,
        }}
      >
        {children}
      </EditAccountContext.Provider>
    </>
  )
}

export default EditAccountProvider
